import useFavouriteStopDetails from "@catchy/app-core/src/useFavouriteStopDetails";
import { Fragment, useMemo } from "react";
import useModalNavigation from "@catchy/app-core/src/useModalNavigation";
import { Dialog, DialogTitle, MenuItem, Transition, TransitionChild } from "@headlessui/react";
import useLocalStorage from "@catchy/app-core/src/useLocalStorage";
import OptionsMenu from "./OptionsMenu";
import IconButton from "./IconButton";
import { formatStopDistance, getPreferredStopName, orderStopsWithNearbyFirst, useNearbyFirst } from "@catchy/app-core";
import StopRow from "./StopRow";
import ReorderFavourites from "./ReorderFavourites";
import CloseCross from "@catchy/assets/svgs/close-cross.svg";
import Star from "@catchy/assets/svgs/star.svg";
const Favourites = ({
  isApple,
  isClient,
  preferredStopNames,
  type,
  filterType = type,
  resolveStopType,
  onStopSelect,
  useStorage = useLocalStorage
}) => {
  const reorderModal = useModalNavigation("reorder");
  const {
    favourites,
    setFavourites,
    details
  } = useFavouriteStopDetails(useStorage);
  const {
    canShowOption: showNearbyOption,
    isNearbyFirstActive,
    requestNearbyFirst,
    showNearbyFirst,
    userLocation
  } = useNearbyFirst({
    storageKey: "showNearbyFavouritesFirst",
    useLocalStorage: useStorage
  });
  const typeFavourites = useMemo(() => {
    return (Array.isArray(favourites) ? favourites : []).map(id => {
      const stop = Object.hasOwn(details, id) ? details[id] : {
        stop_id: id,
        stop_name: `Stop ${id}`
      };
      return {
        ...stop,
        stop_name: getPreferredStopName(preferredStopNames, id, id) || stop.stop_name
      };
    }).filter(stop_0 => {
      const stopType = resolveStopType ? resolveStopType({
        ...stop_0,
        type
      }) : stop_0.stop_type || type;
      return filterType === "all" || stopType === filterType;
    });
  }, [favourites, details, preferredStopNames, resolveStopType, type, filterType]);
  const displayedFavourites = isNearbyFirstActive && userLocation ? orderStopsWithNearbyFirst({
    stops: typeFavourites,
    latitude: userLocation.latitude,
    longitude: userLocation.longitude
  }) : typeFavourites;
  return <>
      <section className="flex flex-col">
        <div className="mb-2 flex justify-between px-[20px]">
          <h2 className="text-3xl font-semibold">Favourites</h2>
          {isClient && (typeFavourites.length > 1 || showNearbyOption) && <OptionsMenu isApple={isApple}>
              {typeFavourites.length > 1 && <MenuItem>
                  <button className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3" onClick={reorderModal.open}>
                    Reorder Favourites…
                  </button>
                </MenuItem>}
              {showNearbyOption && <MenuItem>
                  <button aria-pressed={showNearbyFirst} className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3" onClick={() => {
              requestNearbyFirst(!showNearbyFirst);
            }}>
                    <span>Show nearby first</span>
                    <span className="opacity-60">
                      {showNearbyFirst ? "On" : "Off"}
                    </span>
                  </button>
                </MenuItem>}
            </OptionsMenu>}
        </div>

        <div className="flex flex-col">
          {isClient && (typeFavourites.length ? displayedFavourites.map(stop_1 => <StopRow resolveStopType={resolveStopType} onStopSelect={onStopSelect ? () => onStopSelect(stop_1) : undefined} stop_id={stop_1.stop_id} stop_name={stop_1.stop_name} stop_type={stop_1.stop_type} type={type} distance={isNearbyFirstActive ? formatStopDistance(stop_1.distanceMeters) : null} key={stop_1.stop_id} />) : <div className="mx-[20px] flex justify-start py-4">
                <div className="grid h-6 w-11 shrink-0 place-items-center pt-1">
                  <Star className="h-5 w-5 text-zinc-400 opacity-50" />
                </div>
                <aside className="text-pretty pt-1 text-lg leading-tight opacity-80">
                  Add stops to your favourites by tapping the star icon next to
                  the stop name.
                </aside>
              </div>)}
        </div>
      </section>

      <Transition appear show={reorderModal.isOpen} as={Fragment}>
        <Dialog as="div" onClose={reorderModal.close} className="fixed inset-0 z-30 overflow-y-auto">
          <div className="flex min-h-[var(--vvh)] items-center justify-center overflow-hidden">
            <TransitionChild as={Fragment} enter="transition-[transform,opacity] ease-out duration-200" enterFrom="opacity-0 translate-y-10" enterTo="opacity-100 translate-y-0" leave="transition-[transform,opacity] ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-5" className="absolute inset-0 overflow-y-auto bg-white opacity-100 motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:data-[closed]:opacity-100 motion-reduce:data-[closed]:translate-y-0 dark:bg-black">
              <div>
                <div className="mx-auto flex w-full max-w-md flex-col px-[20px] py-5">
                  <div className="mb-2">
                    <IconButton variant="subtle" label="Close reorder favourites" onClick={reorderModal.close}>
                      <CloseCross className="h-5 w-5" aria-hidden="true" />
                    </IconButton>
                  </div>
                  <DialogTitle as="h2" className="mb-4 text-3xl font-semibold">
                    Reorder Favourites
                  </DialogTitle>
                  <p className="mb-6 text-lg opacity-80">
                    Drag and drop stops to reorder them.
                  </p>
                  <ReorderFavourites typeFavourites={typeFavourites} setFavourites={setFavourites} resolveStopType={resolveStopType} type={type} />
                </div>
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </>;
};
export default Favourites;